import React, { useRef } from "react";

import Container from "../../../../../../../../components/Container";
import TextInput from "../../../../../../../../components/Inputs/InputText";
import SelectInput from "../../../../../../../../components/Inputs/SelectInput";
import TruncatedSelectInput from "../../../../../../../../components/Inputs/SelectInput/TruncatedSelectInput";
import Text from "../../../../../../../../components/Text";

import { ChartType } from "../../../../store/enums";

import { hasEnoughRoomBelowForElements } from "../ChartHooks/utils";

import scatterConfig from "../ChartHooks/ScatterChart/Config";
import useChartOptions from "../ChartHooks/UseChartOptions/useChartOptions";
import OptionSettings, { dataSeriesTooltipText } from "../shared/OptionSettings";

const { dataSeriesRangeOptions } = OptionSettings[ChartType.ScatterChart];

const ScatterChartOptions = () => {
	const optionsRefX = useRef<HTMLDivElement>(null);

	const {
		setYAxisColumnData,
		setXAxisColumnData,
		setDataSeries,
		visualizeOption,
		dataSeriesOptions,
		anyAxisOptions,
		previousVisualizeOption,
	} = useChartOptions(ChartType.ScatterChart);

	const selectedRange =
		(visualizeOption?.dataSeries?.rangeOption &&
			dataSeriesRangeOptions.map((val) => val.value).includes(visualizeOption.dataSeries.rangeOption) &&
			visualizeOption.dataSeries.rangeOption) ||
		scatterConfig.DEFAULT_RANGE_OPTION;

	const DataSeriesOptions = (
		<>
			<SelectInput
				label="Data Series"
				labelProps={{ color: "neutral-9", weight: "semi-bold", size: "paragraph-3" }}
				results={dataSeriesOptions}
				menuPortalTarget={document.body}
				menuPosition="fixed"
				value={visualizeOption?.dataSeries?.column}
				onChange={(option) => setDataSeries("column", option.value)}
				info={{
					title: "Data Series",
					message: dataSeriesTooltipText,
					titleBottomLine: true,
				}}
			/>
			<Container marginTop={1} marginBottom="3px">
				<Container centerVertical marginBottom="5px" width={79}>
					<Text size="caption" color="neutral-9" weight="regular">
						Set Range
					</Text>
				</Container>
				<Container block>
					<SelectInput
						value={selectedRange}
						results={dataSeriesRangeOptions}
						menuPortalTarget={document.body}
						menuPosition="fixed"
						onChange={(option) => setDataSeries("rangeOption", option.value)}
						inputId="data-series-scatter-range"
					/>
				</Container>
			</Container>
		</>
	);

	const YAxisOptions = (
		<>
			<TruncatedSelectInput
				label="Y-Axis"
				labelProps={{ color: "neutral-9", weight: "semi-bold", size: "paragraph-3" }}
				results={anyAxisOptions}
				menuPortalTarget={document.body}
				menuPosition="fixed"
				value={
					visualizeOption?.yAxis?.column ||
					previousVisualizeOption?.yAxis?.column ||
					previousVisualizeOption?.xAxis?.column
				}
				onChange={(option) => {
					setYAxisColumnData("column", option.value);
				}}
			/>
			<Container marginTop={1} marginBottom={1}>
				<Container centerVertical marginBottom="5px" width={79}>
					<Text size="caption" color="neutral-9" weight="regular">
						Set Minimum
					</Text>
				</Container>
				<Container block>
					<TextInput
						type="number"
						value={visualizeOption?.yAxis?.min ?? 0}
						onChange={(value) => setYAxisColumnData("min", value)}
						testId="scatter-y-axis-min"
					/>
				</Container>
			</Container>
			<Container marginBottom={1}>
				<Container centerVertical marginBottom="5px" width={79}>
					<Text size="caption" color="neutral-9" weight="regular">
						Set Maximum
					</Text>
				</Container>
				<Container block>
					<TextInput
						type="number"
						value={visualizeOption?.yAxis?.max ?? 100}
						onChange={(value) => setYAxisColumnData("max", value)}
						testId="scatter-y-axis-max"
					/>
				</Container>
			</Container>
		</>
	);

	const XAxisOptions = (
		<>
			<span ref={optionsRefX}>
				<TruncatedSelectInput
					label="X-Axis"
					labelProps={{ color: "neutral-9", weight: "semi-bold", size: "paragraph-3" }}
					value={
						visualizeOption?.xAxis?.column ||
						previousVisualizeOption?.xAxis?.column ||
						previousVisualizeOption?.yAxis?.column
					}
					results={anyAxisOptions}
					menuPortalTarget={document.body}
					menuPosition="fixed"
					menuPlacement={hasEnoughRoomBelowForElements(optionsRefX.current, anyAxisOptions) ? "auto" : "top"}
					onChange={(option) => {
						setXAxisColumnData("column", option.value);
					}}
				/>
			</span>
			<Container marginTop={1} marginBottom={1}>
				<Container centerVertical width={79}>
					<Text size="caption" color="neutral-9" weight="regular">
						Set Minimum
					</Text>
				</Container>
				<Container block>
					<TextInput
						type="number"
						value={visualizeOption?.xAxis?.min ?? 0}
						onChange={(value) => setXAxisColumnData("min", value)}
						testId="scatter-x-axis-min"
					/>
				</Container>
			</Container>
			<Container marginBottom={1}>
				<Container centerVertical width={79}>
					<Text size="caption" color="neutral-9" weight="regular">
						Set Maximum
					</Text>
				</Container>
				<Container block>
					<TextInput
						type="number"
						value={visualizeOption?.xAxis?.max ?? 100}
						onChange={(value) => setXAxisColumnData("max", value)}
						testId="scatter-x-axis-max"
					/>
				</Container>
			</Container>
		</>
	);

	return (
		<>
			{DataSeriesOptions}
			{YAxisOptions}
			{XAxisOptions}
		</>
	);
};

export default ScatterChartOptions;
